// src/app/resources/page.tsx
'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Skeleton,
  Pagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  FilterList,
  Download as DownloadIcon,
  OpenInNew,
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  School as HandbookIcon,
  Assignment as FormIcon,
  Info as GuidelinesIcon,
  Folder as FolderIcon,
  CalendarToday,
  Visibility,
  GetApp,
} from '@mui/icons-material';

import { getMediaUrl, formatDate } from '@/lib/api';
import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';
import PageHero from '@/components/PageHero';

// File-format / status colours are intentionally hardcoded — they're
// semantic indicators (red=PDF, green=public, etc.) and shouldn't shift
// with the light/dark theme.
const FILE_TYPE_COLOR = {
  pdf:    '#E53935',
  doc:    '#1E88E5',
  excel:  '#43A047',
  image:  '#FB8C00',
  other:  '#757575',
};
const ACCESS_PUBLIC_COLOR     = '#43A047';
const ACCESS_RESTRICTED_COLOR = '#FB8C00';

// Temporary mock hook — replace with real useResources when available
const useResources = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [resources, setResources] = React.useState<Resource[]>([]);

  React.useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const apiBase = process.env.NEXT_PUBLIC_STRAPI_API_URL || `${process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337'}/api`;
        const response = await fetch(`${apiBase}/resources?populate=*`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          setResources(data.data);
        } else {
          setResources([]);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to fetch resources:', err);
        setError(err instanceof Error ? err.message : 'Failed to load resources');
        setResources([]);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  return { resources, loading, error };
};

interface Resource {
  id: number;
  title: string;
  description?: string;
  resourceType: string;
  externalLink?: string;
  category?: string;
  accessLevel?: string;
  version?: string;
  lastUpdated?: string;
  fileSize?: string;
  slug?: string;
  relatedThemes?: any[];
  file?: {
    url: string;
    name: string;
    ext: string;
    mime: string;
  } | null;
}

// =====================
// STYLED COMPONENTS
// =====================

const FilterContainer = styled(Paper)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: colors.background.elevated,
    border: `1px solid ${colors.border.primary}`,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  };
});

const ResourceCard = styled(Card)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-4px)',
      boxShadow: `0 12px 40px ${colors.effects.glow}`,
    },
  };
});

const StatCard = styled(Paper)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: colors.background.elevated,
    border: `1px solid ${colors.border.primary}`,
    padding: theme.spacing(2),
    textAlign: 'center',
    minWidth: '120px',
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-2px)',
      transition: 'all 0.3s ease',
    },
  };
});

// =====================
// SUB-COMPONENTS
// =====================

interface ResourceItemProps {
  resource: Resource;
  onClick: (resource: Resource) => void;
}

const ResourceItem = React.memo<ResourceItemProps>(({ resource, onClick }) => {
  const colors = useColorTheme();

  const handleClick = useCallback(() => {
    onClick(resource);
  }, [resource, onClick]);

  const getResourceIcon = (type: string, fileUrl?: string) => {
    if (fileUrl) {
      const extension = fileUrl.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'pdf':  return <PdfIcon      sx={{ color: FILE_TYPE_COLOR.pdf   }} />;
        case 'doc':
        case 'docx': return <DocumentIcon sx={{ color: FILE_TYPE_COLOR.doc   }} />;
        case 'xls':
        case 'xlsx': return <ExcelIcon    sx={{ color: FILE_TYPE_COLOR.excel }} />;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':  return <ImageIcon    sx={{ color: FILE_TYPE_COLOR.image }} />;
        default:     return <FileIcon     sx={{ color: FILE_TYPE_COLOR.other }} />;
      }
    }
    switch (type) {
      case 'Student Handbook':
      case 'Handbook':         return <HandbookIcon   sx={{ color: FILE_TYPE_COLOR.doc   }} />;
      case 'Application Form': return <FormIcon      sx={{ color: FILE_TYPE_COLOR.excel }} />;
      case 'Guidelines':
      case 'Guide':            return <GuidelinesIcon sx={{ color: FILE_TYPE_COLOR.image }} />;
      default:                 return <DocumentIcon   sx={{ color: FILE_TYPE_COLOR.other }} />;
    }
  };

  const handleDownload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (resource.file?.url) {
        window.open(getMediaUrl(resource.file), '_blank');
      } else if (resource.externalLink) {
        window.open(resource.externalLink, '_blank', 'noopener,noreferrer');
      } else {
        alert('This resource is not available for download.');
      }
    } catch (error) {
      console.error('Error downloading resource:', error);
      alert('Error downloading file. Please try again later.');
    }
  }, [resource]);

  const isPublic = resource.accessLevel === 'Public';
  const accessColor = isPublic ? ACCESS_PUBLIC_COLOR : ACCESS_RESTRICTED_COLOR;

  return (
    <ResourceCard onClick={handleClick}>
      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Chip
            icon={getResourceIcon(resource.resourceType, resource.file?.url)}
            label={resource.resourceType}
            size="small"
            sx={{
              background: withOpacity(colors.brand.accent.main, 0.2),
              color: colors.brand.accent.dark,
              border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
              fontSize: '0.75rem',
            }}
          />
          {resource.category && (
            <Chip
              label={resource.category}
              size="small"
              sx={{
                background: withOpacity(colors.brand.accent.main, 0.1),
                color: colors.brand.accent.dark,
                border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: colors.brand.accent.dark,
            fontWeight: 600,
            marginBottom: 1,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {resource.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            lineHeight: 1.6,
            marginBottom: 2,
            flexGrow: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {resource.description}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {resource.version && (
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Version: {resource.version}
            </Typography>
          )}
          {resource.lastUpdated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: '0.8rem', color: colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                Updated: {formatDate(resource.lastUpdated)}
              </Typography>
            </Box>
          )}
          {resource.fileSize && (
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Size: {resource.fileSize}
            </Typography>
          )}
        </Box>

        {resource.accessLevel && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={resource.accessLevel}
              size="small"
              sx={{
                background: withOpacity(accessColor, 0.15),
                color: accessColor,
                border: `1px solid ${accessColor}`,
                fontSize: '0.7rem',
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 'auto', pt: 2, borderTop: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}` }}>
          {(resource.file?.url || resource.externalLink) ? (
            <Button
              variant="contained"
              size="small"
              fullWidth
              startIcon={resource.file?.url ? <DownloadIcon /> : <OpenInNew />}
              onClick={handleDownload}
              sx={{
                background: colors.gradients.primary,
                color: colors.brand.accent.contrastText,
                fontWeight: 600,
                fontSize: '0.8rem',
                '&:hover': {
                  background: colors.gradients.primary,
                  filter: 'brightness(1.1)',
                },
              }}
            >
              {resource.file?.url ? 'Download' : 'View'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              disabled
              sx={{
                color: colors.text.disabled,
                borderColor: colors.border.primary,
                fontSize: '0.8rem',
              }}
            >
              Not Available
            </Button>
          )}
        </Box>
      </CardContent>
    </ResourceCard>
  );
});

ResourceItem.displayName = 'ResourceItem';

const LoadingSkeleton = React.memo(() => {
  const colors = useColorTheme();
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{
            background: colors.gradients.card,
            border: `1px solid ${colors.border.primary}`,
            height: 400,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="rectangular" height={32} width="45%" sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 2 }} />
              <Skeleton variant="text"        height={28} width="85%" sx={{ bgcolor: withOpacity(colors.text.primary, 0.15), mb: 1 }} />
              <Skeleton variant="text"        height={16}             sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 1 }} />
              <Skeleton variant="text"        height={16} width="70%" sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 2 }} />
              <Skeleton variant="rectangular" height={36}             sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const ResourcesPage: React.FC = () => {
  const colors = useColorTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const { resources: allResources, loading, error } = useResources();

  const categories = useMemo(() => {
    const cats = allResources.map(resource => resource.category).filter(Boolean);
    return [...new Set(cats)];
  }, [allResources]);

  const filteredResources = useMemo(() => {
    return allResources.filter(resource => {
      const matchesSearch = searchQuery === '' ||
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType     = selectedType     === '' || resource.resourceType === selectedType;
      const matchesCategory = selectedCategory === '' || resource.category     === selectedCategory;
      return matchesSearch && matchesType && matchesCategory;
    });
  }, [allResources, searchQuery, selectedType, selectedCategory]);

  const paginatedResources = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredResources.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredResources, currentPage]);

  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);

  const handleResourceClick = useCallback((resource: Resource) => {
    if (resource.file?.url) {
      window.open(getMediaUrl(resource.file), '_blank');
    } else if (resource.externalLink) {
      window.open(resource.externalLink, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: colors.text.primary,
      '& fieldset':                     { borderColor: colors.border.primary },
      '&:hover fieldset':               { borderColor: colors.border.accent },
      '&.Mui-focused fieldset':         { borderColor: colors.border.accent },
    },
  } as const;

  const selectSx = {
    color: colors.text.primary,
    '& .MuiOutlinedInput-notchedOutline':              { borderColor: colors.border.primary },
    '&:hover .MuiOutlinedInput-notchedOutline':        { borderColor: colors.border.accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline':  { borderColor: colors.border.accent },
  } as const;

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      <PageHero
        label="Resources"
        title="Resources"
        subtitle="Access program materials, handbooks, application forms, and collaboration guidelines"
        icon={<FolderIcon />}
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Statistics */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
          <StatCard>
            <FolderIcon sx={{ color: colors.brand.accent.dark, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.dark, fontWeight: 700 }}>
              {allResources.length}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Total Resources
            </Typography>
          </StatCard>
          <StatCard>
            <Visibility sx={{ color: colors.brand.accent.dark, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.dark, fontWeight: 700 }}>
              {allResources.filter(r => r.accessLevel === 'Public').length}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Public Access
            </Typography>
          </StatCard>
          <StatCard>
            <GetApp sx={{ color: colors.brand.accent.dark, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.dark, fontWeight: 700 }}>
              {new Set(allResources.map(r => r.resourceType)).size}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Resource Types
            </Typography>
          </StatCard>
        </Box>

        {/* Filters */}
        <FilterContainer>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.brand.accent.dark }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.text.disabled }}>Resource Type</InputLabel>
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  label="Resource Type"
                  sx={selectSx}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Student Handbook">Student Handbooks</MenuItem>
                  <MenuItem value="Application Form">Application Forms</MenuItem>
                  <MenuItem value="Guidelines">Guidelines</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.text.disabled }}>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                  sx={selectSx}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchQuery('');
                  setSelectedType('');
                  setSelectedCategory('');
                  setCurrentPage(1);
                }}
                sx={{
                  color: colors.brand.accent.dark,
                  borderColor: colors.brand.accent.main,
                  py: 1.5,
                  '&:hover': {
                    background: withOpacity(colors.brand.accent.main, 0.1),
                    borderColor: colors.brand.accent.main,
                  },
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </FilterContainer>

        {/* Content */}
        {loading ? (
          <Grid container spacing={3}>
            <LoadingSkeleton />
          </Grid>
        ) : error ? (
          <Paper sx={{
            p: 4,
            textAlign: 'center',
            background: colors.gradients.card,
            border: `1px solid ${colors.border.primary}`,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 2 }}>Unable to Load Resources</Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>{error}</Typography>
          </Paper>
        ) : paginatedResources.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {paginatedResources.map((resource) => (
                <Grid key={resource.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <ResourceItem resource={resource} onClick={handleResourceClick} />
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_e, page) => setCurrentPage(page)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: colors.text.primary,
                      borderColor: colors.border.primary,
                      '&:hover': {
                        backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
                        borderColor: colors.brand.accent.main,
                      },
                      '&.Mui-selected': {
                        backgroundColor: colors.brand.accent.main,
                        color: colors.brand.accent.contrastText,
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Paper sx={{
            p: 4,
            textAlign: 'center',
            background: colors.gradients.card,
            border: `1px solid ${colors.border.primary}`,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>No Resources Found</Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              {searchQuery || selectedType || selectedCategory
                ? 'Try adjusting your search criteria or clearing filters.'
                : 'Resources will appear here once they are uploaded to the system.'}
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ResourcesPage;
